import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import * as authApi from '@/api/auth';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.forgotPassword({ email });
      setSubmitted(true);
    } catch {
      // Always show the same message to avoid email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-900 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <img src="/logo.png" alt="Ashcourt" className="mx-auto mb-5 h-12 w-auto" />
          <h1 className="text-2xl font-bold text-white">Reset password</h1>
          <p className="mt-1 text-sm text-slate-400">
            Enter your email and we'll send a reset link.
          </p>
        </div>

        <div className="rounded-xl border border-slate-700 bg-navy-800 p-8 shadow-lg [&_.input-field]:bg-navy-900 [&_.input-field]:border-slate-600 [&_.input-field]:text-white [&_.input-field]:placeholder-slate-400 [&_label]:text-slate-200">
          {submitted ? (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-slate-300">
                If that email is registered, a reset link has been sent. Check your inbox.
              </p>
              <Link to="/login" className="text-sm text-primary-400 hover:text-primary-300">
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              {error && (
                <div role="alert" className="rounded-lg border border-red-800 bg-red-900/30 px-4 py-3 text-sm text-red-400">
                  {error}
                </div>
              )}
              <Input
                label="Email address"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
              <Button type="submit" loading={loading} className="w-full">
                Send reset link
              </Button>
              <p className="text-center text-sm text-slate-400">
                <Link to="/login" className="text-primary-400 hover:text-primary-300">
                  Back to sign in
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
